import { LOCALE_ID, Provider, EnvironmentProviders } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouteReuseStrategy } from '@angular/router';
import { Overlay, CloseScrollStrategy } from '@angular/cdk/overlay';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';

import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';

import { ApiInterceptor } from '@common/api.interceptor';

import { CookieService } from 'ngx-cookie-service';

import { Events } from '@providers/common/events.service';
import { DataService } from '@providers/common/data.service';
import { DataResolverService } from '@resolver/data-resolver/data-resolver.service';
import { UtilityFunctionService } from '@providers/common/utilityFunction.service';
import { URLService } from '@providers/common/url.service';
import { AlertasService } from '@providers/alertas-service/alertas.service';
import { AlertProvider } from '@providers/alerts/alert.service';
import { ToastProvider } from '@providers/toast/toast.service';
import { ApiProvider } from '@providers/common/api.service';
import { AppStorageProvider } from '@providers/common/app-storage.service';
import { ConsultFieldsService } from '@providers/consult-fields-service/consult-fields.service';
import { ConsultasCivarService } from '@providers/consultas-civar-service/consultas-civar.service';
import { DashboardsService } from '@providers/dashboards/dashboards.service';
import { DashboardBSCServiceService } from '@providers/dashboardBSC/dashboard-bscservice.service';
import { ErrorService } from '@providers/error-service/error.service';
import { ExchangeProvider } from '@providers/exchange/exchange.service';
import { FavoriteHomeProvider } from '@providers/favorite-home/favorite-home-service.service';
import { HomeProvider } from '@providers/home/home.service';
import { InvestorRelationsService } from '@providers/investor-relations-service/investor-relations.service';
import { LoginProvider } from '@providers/login/login.service';
import { MatrizEstatusService } from '@providers/matriz-estatus-service/matriz-estatus.service';
import { MultipleVinService } from '@providers/multiple-vin-service/multiple-vin.service';
import { NotificationsProvider } from '@providers/notifications/notifications.service';
import { PatiosService } from '@providers/patios/patios.service';
import { PreferencesProvider } from '@providers/preferences/preferences.service';
import { ReportListProvider } from '@providers/report-list/report-list.service';
import { ReportMenuProvider } from '@providers/report-menu/report-menu.service';
import { ReportViewerProvider } from '@providers/report-viewer/report-viewer.service';
import { ScoreProvider } from '@providers/score/score.service';
import { SessionProvider } from '@providers/session/session.service';
import { TractoresService } from '@providers/tractores-service/tractores-service.service';
import { TrakingMapProvider } from '@providers/traking-map/traking-map.service';
import { TravelDetailProvider } from '@providers/travel-detail/travel-detail.service';
import { TravelListProvider } from '@providers/travel-list/travel-list.service';
import { UserProvider } from '@providers/user/user.service';
import { VehicleDetailProvider } from '@providers/vehicle-detail/vehicle-detail.service';
import { ShipProvider } from '@providers/ship-service/ship.service';
import { RateTripsService } from '@providers/rate-trip/rate-trips.service';
import { TranslateConfigService } from '@providers/common/translate-config.service';
import { IdiomasAppService } from '@providers/idiomas/idiomas-app.service';
import { AppVersionCheckService } from '@providers/common/appVersionCheck.service';
import { ManageUsersService } from '@providers/manage-users/manage-users.service';
import { ChatAIPopupService } from '@providers/chat/ai-chat.service';
import { UserPreferencesService } from '@providers/preferences/user-preferences.service';

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
    return () => overlay.scrollStrategies.close();
}

/**
 * Providers globales de la app. Los comparten appConfig (app.config.ts) y la configuracion
 * de los tests (src/app/testing/smoke-test.ts) para que ambos vean lo mismo.
 * HttpClient se provee aparte: XHR en la app, HttpClientTesting en los tests.
 */
export const APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
    CookieService,
    Events,
    DataService,
    DataResolverService,
    UtilityFunctionService,
    URLService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
        provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
        useFactory: scrollFactory,
        deps: [Overlay],
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiInterceptor,
        multi: true,
    },
    { provide: LOCALE_ID, useValue: 'es-MX' },
    DatePipe,
    ApiProvider,
    AppStorageProvider,
    FavoriteHomeProvider,
    HomeProvider,
    LoginProvider,
    NotificationsProvider,
    SessionProvider,
    UserProvider,
    TravelListProvider,
    ScoreProvider,
    TravelDetailProvider,
    TrakingMapProvider,
    VehicleDetailProvider,
    ReportListProvider,
    ReportMenuProvider,
    ReportViewerProvider,
    PreferencesProvider,
    AlertProvider,
    ToastProvider,
    ExchangeProvider,
    MultipleVinService,
    ConsultFieldsService,
    ConsultasCivarService,
    MatrizEstatusService,
    AlertasService,
    TractoresService,
    ErrorService,
    DashboardsService,
    DashboardBSCServiceService,
    PatiosService,
    ShipProvider,
    InvestorRelationsService,
    RateTripsService,
    TranslateConfigService,
    IdiomasAppService,
    AppVersionCheckService,
    ManageUsersService,
    ChatAIPopupService,
    UserPreferencesService,
    provideIonicAngular({
        mode: 'md',
        scrollPadding: false,
        scrollAssist: true,
        // Asigna componentProps de modales/popovers con ComponentRef.setInput
        // (en lugar de Object.assign): cada clave debe ser un input del componente
        useSetInputAPI: true,
    }),
];
